import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Dialog, DialogContent, Typography, Box, Button } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import Slide from "@material-ui/core/Slide";
import { useCheckoutPackageOffer } from "~/hooks/useCheckoutPackageOffer";
import usePolicyActive from "~/hooks/usePolicyActive";
import useDateFormatter from "~/hooks/useDateFormatter";
import useBottomSheetClasses, { BOTTOM_SHEET_FIXED_CLASS } from "~/components/common/bottomSheet/useBottomSheetClasses";
import Utils from "~/helpers/Utils";
import { mixpanelTrackEvent } from "~/utils/mixpanel";
import {
	sortPackageOptions,
	getEligibleClientPackages,
	getEarliestValidExpiry,
	formatDateDDMMYYYY,
	buildPackageChoice,
	clientPackageInfo,
	clientPackageTotalPrice,
} from "~/utils/packages";

const SAGE = "#4A6157";
const W_DEFAULT = "#672E37";

const Transition = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

const PackageOfferModal = ({
	open,
	onClose,
	onAccept,
	onDecline,
	onFinishAllServices,
	recommendedPackages: propsRecommendedPackages,
	services,
	currentServiceIndex: propCurrentServiceIndex,
	servicesFormatted = [],
	currency,
	// New props for anonymous flow (REQ-03)
	clientPackages,
	onChoicesChange,
	// Fluxo sequencial (um serviço por vez): recebe a escolha pronta e a posição na fila
	onChoose,
	stepIndex = 0,
	stepTotal = 1,
}) => {
	const { t, i18n } = useTranslation();
	const { formatShortTime } = useDateFormatter();
	const sheetClasses = useBottomSheetClasses();
	const { recommendedPackages: contextRecommendedPackages } = useCheckoutPackageOffer();
	// Sem política de cancelamento ativa, o pacote pendente não é cobrado no agendamento
	const isPolicyActive = usePolicyActive();
	const { business } = useSelector((store) => store.business);
	const { user } = useSelector((store) => store.auth);
	const meStore = useSelector((store) => store.me?.me);
	const me = Utils.safeParseJSON("@prit-web/meData", meStore);
	const currentUser = me || user || {};

	const recommendedPackages = propsRecommendedPackages || contextRecommendedPackages;

	const [componentView, setComponentView] = useState("only");
	const [selectedPackageId, setSelectedPackageId] = useState(null);
	const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
	const [isActivating, setIsActivating] = useState(false);
	// For anonymous flow: selected option id (format: "client:<id>", "purchase:<id>", "avulso")
	const [selectedOptionId, setSelectedOptionId] = useState(null);

	// isAnonymousFlow: true when clientPackages prop is provided (array, even if empty)
	const isAnonymousFlow = Array.isArray(clientPackages);

	// Reset state when modal opens
	useEffect(() => {
		if (open) {
			setCurrentServiceIndex(propCurrentServiceIndex !== undefined ? propCurrentServiceIndex : 0);
			setComponentView("only");
			setSelectedPackageId(null);
			setSelectedOptionId(null);
			setIsActivating(false);
		}
	}, [open, propCurrentServiceIndex]);

	const handleComponentView = (view) => {
		setComponentView(view);
	};

	// ─── Legacy flow helpers ──────────────────────────────────────────────────

	const getCurrentServicePackages = () => {
		if (!services || !services[currentServiceIndex]) return [];
		const currentService = services[currentServiceIndex];
		return (recommendedPackages || []).filter((pkg) => pkg.service?.id === currentService.id);
	};

	const getCurrentServiceBestOffer = () => {
		const currentPackages = getCurrentServicePackages();
		return currentPackages.length > 0 ? currentPackages[0] : null;
	};

	const trackPackageChoice = (opcao) => {
		mixpanelTrackEvent("Pacote_Escolha", { Opcao: opcao }, currentUser, business);
	};

	const handleAcceptOffer = (packageId) => {
		if (isActivating) return;
		setIsActivating(true);
		if (!isAnonymousFlow) trackPackageChoice("com_pacote");
		onAccept(packageId, servicesFormatted);
		onClose();
	};

	const handleDeclineOffer = () => {
		if (!isAnonymousFlow) trackPackageChoice("sem_pacote");
		if (typeof onDecline === "function") onDecline();
		onClose();
	};

	const currentServicePackages = getCurrentServicePackages();
	const currentBestOffer = getCurrentServiceBestOffer();

	// ─── Anonymous flow helpers ───────────────────────────────────────────────

	const getCurrentContext = () => {
		const currentService = services && services[currentServiceIndex];
		if (!currentService) return null;
		const scheduled = servicesFormatted?.find((s) => s?.service?.id === currentService.id);
		const bookingDate = scheduled?.time?.start || scheduled?.dateStart || new Date().toISOString();
		const professionalId = scheduled?.professional?.id || scheduled?.professional_id;
		const businessPkgsForService = (recommendedPackages || []).filter(
			(pkg) => pkg.service?.id === currentService.id
		);
		const eligibleClient = getEligibleClientPackages(
			clientPackages,
			currentService.id,
			professionalId,
			bookingDate
		);
		const options = sortPackageOptions(eligibleClient, businessPkgsForService, currentService);
		return { currentService, scheduled, bookingDate, eligibleClient, options };
	};

	// Pre-select first option when options become available (anonymous flow)
	useEffect(() => {
		if (!open || !isAnonymousFlow) return;
		if (selectedOptionId) return;
		const ctx = getCurrentContext();
		if (ctx?.options.length) {
			setSelectedOptionId(ctx.options[0].id);
		}
	}, [open, currentServiceIndex, isAnonymousFlow, clientPackages, recommendedPackages]);

	const emitChoice = (option) => {
		const ctx = getCurrentContext();
		if (!ctx) return;
		const choice = buildPackageChoice(option, ctx.bookingDate, i18n.language);
		if (choice.type === "client") trackPackageChoice("usar_pacote");
		else if (choice.type === "purchase") trackPackageChoice("com_pacote");
		else trackPackageChoice(ctx.eligibleClient.length > 0 ? "nao_usar_pacote" : "sem_pacote");

		if (onChoose) {
			onChoose(choice, ctx.currentService);
			return;
		}
		if (onChoicesChange) {
			onChoicesChange({ [ctx.currentService.id]: choice });
		}
		// Use existing onAccept/onDecline pathway to maintain outer state
		if (choice.type === "avulso") {
			handleDeclineOffer();
		} else if (choice.type === "client") {
			handleAcceptOffer(option.id);
		} else {
			handleAcceptOffer(option.offer?.id);
		}
	};

	// Pacote_Oferta_Exibida: uma vez por serviço a cada abertura da folha, quando já há pacotes para mostrar
	const trackedOfferServiceId = useRef(null);
	const currentServiceId = services?.[currentServiceIndex]?.id;
	const offeredPackagesCount = isAnonymousFlow
		? (getCurrentContext()?.options || []).filter((o) => o.type !== "avulso").length
		: currentServicePackages.length;

	useEffect(() => {
		if (!open) {
			trackedOfferServiceId.current = null;
			return;
		}
		if (!currentServiceId || offeredPackagesCount === 0) return;
		if (trackedOfferServiceId.current === currentServiceId) return;
		trackedOfferServiceId.current = currentServiceId;
		mixpanelTrackEvent(
			"Pacote_Oferta_Exibida",
			{ Quantidade_Pacotes: offeredPackagesCount },
			currentUser,
			business
		);
	}, [open, currentServiceId, offeredPackagesCount]);

	const handleAnonymousConfirm = () => {
		if (!selectedOptionId || selectedOptionId === "avulso") return;
		const ctx = getCurrentContext();
		const chosen = ctx?.options.find((o) => o.id === selectedOptionId);
		if (!chosen) return;
		emitChoice(chosen);
	};

	// ─── Anonymous flow render ────────────────────────────────────────────────

	const componentAnonymousFlow = () => {
		const ctx = getCurrentContext();
		if (!ctx) return null;
		const { currentService, scheduled, bookingDate, eligibleClient, options } = ctx;

		const hasClientPkg = eligibleClient.length > 0;
		// Um único pacote da cliente e nada mais para escolher: o card não precisa de radio
		const singleClientOption = hasClientPkg && options.length === 1;
		const W = W_DEFAULT;

		const selectedOption = options.find((o) => o.id === selectedOptionId);
		const isPurchaseSelected = selectedOption?.type === "purchase";

		const formatMoney = (amount) => {
			if (!amount && amount !== 0) return "";
			return amount.toLocaleString("pt-BR", { style: "currency", currency: currency || "BRL" });
		};

		// Service header data
		const dateObj = bookingDate ? new Date(bookingDate) : null;
		const dateFmt = dateObj
			? dateObj.toLocaleDateString(i18n.language, { day: "2-digit", month: "2-digit" })
			: null;
		// mesmo formato de hora do passo 3 (1:00 PM / 13:00)
		const timeFmt = dateObj ? formatShortTime(dateObj) : null;
		const profName = scheduled?.professional?.name || null;
		const svcName = currentService?.name || currentService?.serviceName || "";

		const avulsoPrice = currentService?.price ? currentService.price / 100 : 0;

		return (
			<>
				{/* Title */}
				<Typography
					variant="h6"
					component="h2"
					style={{ fontWeight: 800, color: "#1A1A1E", marginBottom: 10, fontSize: "clamp(18px, 2vw, 22px)", textAlign: "left", lineHeight: 1.3 }}>
					{hasClientPkg ? t("package_offer_title_use") : t("package_offer_title_buy")}
				</Typography>

				{/* Service header */}
				<div style={{ marginBottom: 16, textAlign: "left" }}>
					<div style={{ fontSize: 14, fontWeight: 700, color: "#1A1A1E", lineHeight: 1.35 }}>
						{svcName}
					</div>
					{(dateFmt || timeFmt || profName) && (
						<div style={{ fontSize: 12.5, color: "#888", marginTop: 2 }}>
							{dateFmt && timeFmt ? t("package_offer_date_time", { date: dateFmt, time: timeFmt }) : dateFmt || timeFmt || ""}
							{profName ? ` · ${t("package_offer_with_professional", { name: profName })}` : ""}
						</div>
					)}
					{stepTotal > 1 && (
						<div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
							{t("package_offer_service_step", { current: stepIndex + 1, total: stepTotal })}
						</div>
					)}
				</div>

				{/* Option cards */}
				{options.filter((o) => o.type !== "avulso").map((option) => {
					const isSelected = singleClientOption || selectedOptionId === option.id;
					const borderColor = isSelected ? W : "#E0DDD8";

					if (option.type === "client") {
						const pkg = option.clientPackage;
						const { name, quantity } = clientPackageInfo(pkg);
						const expiry = getEarliestValidExpiry(pkg, bookingDate);
						const expiryStr = expiry ? formatDateDDMMYYYY(expiry, i18n.language) : null;
						const isPending = pkg.paymentStatus !== "completed" && isPolicyActive;

						return (
							<Box
								key={option.id}
								onClick={() => setSelectedOptionId(option.id)}
								style={{
									border: `${isSelected ? 2 : 1}px solid ${borderColor}`,
									borderRadius: 12,
									padding: "12px 14px",
									marginBottom: 10,
									cursor: "pointer",
									background: isSelected ? "#FEF9FA" : "#fff",
									display: "flex",
									gap: 10,
									alignItems: "flex-start",
								}}>
								{singleClientOption ? (
									<svg width="19" height="19" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
										<path d="M3.5 7.5L12 3.5l8.5 4v9L12 20.5l-8.5-4v-9z" stroke={SAGE} strokeWidth="1.8" strokeLinejoin="round" />
										<path d="M3.5 7.5L12 11.6l8.5-4.1M12 11.6v8.9" stroke={SAGE} strokeWidth="1.8" strokeLinejoin="round" />
									</svg>
								) : (
									<CheckCircleIcon style={{ color: isSelected ? W : "#ccc", flexShrink: 0, marginTop: 2, fontSize: 20 }} />
								)}
								<div style={{ flex: 1 }}>
									<div style={{ fontSize: 11, fontWeight: 700, color: "#999", letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>
										{t("package_offer_your_package")}
									</div>
									<div style={{ fontSize: 14, fontWeight: 700, color: "#1A1A1E" }}>{name}</div>
									<div style={{ fontSize: 12.5, color: "#888", marginTop: 2 }}>
										{t("package_offer_available_sessions", { remaining: pkg.remainingSessions, total: quantity })}
										{expiryStr ? ` · ${t("package_offer_valid_until", { date: expiryStr })}` : ""}
									</div>
									{isPending && (
										<div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, fontWeight: 700, color: W, marginTop: 6 }}>
											<span>{t("package_offer_to_pay")}</span>
											<span>{formatMoney(clientPackageTotalPrice(pkg) / 100)}</span>
										</div>
									)}
								</div>
							</Box>
						);
					}

					if (option.type === "purchase") {
						const offer = option.offer;
						const totalPrice = formatMoney(offer.price / 100);
						const pps = formatMoney(option.pricePerSession);
						const savings = option.savings > 0 ? formatMoney(option.savings) : null;
						const qty = offer.quantity || 1;
						const months = offer.bookingExpirationDays
							? Math.round(offer.bookingExpirationDays / 30)
							: null;

						return (
							<Box
								key={option.id}
								onClick={() => setSelectedOptionId(option.id)}
								style={{
									border: `${isSelected ? 2 : 1}px solid ${borderColor}`,
									borderRadius: 12,
									padding: "12px 14px",
									marginBottom: 10,
									cursor: "pointer",
									background: isSelected ? "#FEF9FA" : "#fff",
									display: "flex",
									gap: 10,
									alignItems: "flex-start",
								}}>
								<CheckCircleIcon style={{ color: isSelected ? W : "#ccc", flexShrink: 0, marginTop: 3, fontSize: 20 }} />
								<div style={{ flex: 1 }}>
									{/* Row 1: qty (left) + price/session (right) */}
									<div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
										<span style={{ fontSize: 14, fontWeight: 700, color: "#1A1A1E" }}>
											{t(qty === 1 ? "package_offer_appointment_singular" : "package_offer_appointment_plural", { quantity: qty })}
										</span>
										<span style={{ fontSize: 13.5, fontWeight: 600, color: W, flexShrink: 0 }}>
											{t("package_offer_price_each", { price: pps })}
										</span>
									</div>
									{/* Row 2: savings */}
									{savings && (
										<div style={{ fontSize: 13, color: SAGE, fontWeight: 600, marginTop: 3 }}>
											{t("package_offer_savings", { amount: savings })}
										</div>
									)}
									{/* Row 3: validity + total */}
									<div style={{ fontSize: 12, color: "#999", marginTop: 3 }}>
										{months
											? t(months === 1 ? "package_offer_validity_month" : "package_offer_validity_months", { months, total: totalPrice })
											: t("package_offer_total", { total: totalPrice })}
									</div>
								</div>
							</Box>
						);
					}

					return null;
				})}

				{/* Purchase upsell note */}
				{isPurchaseSelected && selectedOption?.offer && (
					<Typography style={{ fontSize: 13, color: "#888", margin: "8px 0 16px", textAlign: "left" }}>
						{t("package_offer_pay_now_note", { price: formatMoney(selectedOption.offer.price / 100) })}
					</Typography>
				)}

				{/* Two-button footer */}
				<div style={{ display: "flex", gap: 10, marginTop: isPurchaseSelected ? 0 : 16 }}>
					{/* Sem pacote */}
					<Button
						onClick={() => {
							setSelectedOptionId("avulso");
							emitChoice({ type: "avulso" });
						}}
						style={{
							flex: 1,
							background: "#F2EDE6",
							color: "#333",
							borderRadius: 30,
							textTransform: "none",
							boxShadow: "none",
							border: "none",
							padding: "10px 0",
						}}>
						<span style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.3 }}>
							<span style={{ fontSize: 15, fontWeight: 700 }}>
								{hasClientPkg ? t("package_offer_dont_use_package") : t("package_offer_without_package")}
							</span>
							<span style={{ fontSize: 12, fontWeight: 400, color: "#888" }}>
								{formatMoney(avulsoPrice)}
							</span>
						</span>
					</Button>

					{/* Com pacote */}
					<Button
						variant="contained"
						onClick={() => {
							if (singleClientOption) {
								emitChoice(options[0]);
								return;
							}
							handleAnonymousConfirm();
						}}
						disabled={!singleClientOption && (!selectedOptionId || selectedOptionId === "avulso")}
						style={{
							flex: 1,
							backgroundColor: W,
							color: "#fff",
							borderRadius: 30,
							textTransform: "none",
							boxShadow: "none",
							border: "none",
							padding: "10px 0",
							opacity: !singleClientOption && (!selectedOptionId || selectedOptionId === "avulso") ? 0.5 : 1,
						}}>
						<span style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.3 }}>
							<span style={{ fontSize: 15, fontWeight: 700 }}>
								{hasClientPkg ? t("package_offer_use_package") : t("package_offer_with_package")}
							</span>
							{isPurchaseSelected && selectedOption?.pricePerSession && (
								<span style={{ fontSize: 12, fontWeight: 400 }}>
									{t("package_offer_price_each", { price: formatMoney(selectedOption.pricePerSession) })}
								</span>
							)}
						</span>
					</Button>
				</div>
			</>
		);
	};

	// ─── Legacy flow renders ──────────────────────────────────────────────────

	const formatPrice = (price) => {
		return (price / 100).toLocaleString("pt-BR", {
			style: "currency",
			currency: currency,
		});
	};

	const formatPackageName = (packageData) => {
		const serviceName = packageData.service?.name || packageData.service?.serviceName || t("Service");
		const quantity = packageData.quantity || 1;
		const sessionWord = quantity === 1 ? t("session") : t("sessions");
		return `${t("Package")} ${serviceName} - ${quantity} ${sessionWord}`;
	};

	const componentOnlyPackage = () => {
		const currentService = services[currentServiceIndex];
		const selectedSchedule = servicesFormatted?.find((s) => s?.service?.id === currentService?.id) || null;
		const serviceName =
			currentService?.serviceName ||
			currentService?.service?.serviceName ||
			currentService?.service?.name ||
			t("Service");
		const rawDate = selectedSchedule?.time?.start || selectedSchedule?.date || selectedSchedule?.dateStart;
		const dateObj = rawDate ? new Date(rawDate) : null;
		const dateStr = dateObj ? dateObj.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) : t("date");
		const timeStr = dateObj
			? new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(dateObj)
			: "--:--";
		const professionalName =
			selectedSchedule?.professional?.name ||
			currentService?.professional?.name ||
			currentService?.professionalName ||
			t("professional");

		return (
			<>
				<Typography
					variant="h6"
					component="h2"
					style={{
						fontWeight: "bold",
						color: "#333",
						marginBottom: "12px",
						fontSize: "clamp(22px, 2vw, 26px)",
					}}>
					{(() => {
						const qty = currentBestOffer?.quantity || 1;
						const price = currentBestOffer?.price || 0;
						const sessKey = qty === 1 ? "session" : "sessions";
						const pkgCount = currentServicePackages?.length || 0;
						if (pkgCount <= 1) {
							return t("packageOfferSingle", {
								serviceName,
								date: dateStr,
								time: timeStr,
								professional: professionalName,
								qty,
								sessionWord: t(sessKey),
								price: formatPrice(price),
							});
						}
						return t("packageOfferMultiple", {
							serviceName,
							date: dateStr,
							time: timeStr,
							professional: professionalName,
							count: pkgCount,
						});
					})()}
				</Typography>
				{currentServicePackages && currentServicePackages.length === 1 && (
					<Box
						style={{
							backgroundColor: "#fff",
							borderRadius: "8px",
							padding: "16px",
							marginBottom: "24px",
							border: "1px solid #e9ecef",
						}}>
						<div style={{ display: "flex", justifyContent: "start", alignItems: "center" }}>
							<Typography
								variant="subtitle1"
								style={{
									fontWeight: "bold",
									color: "#333",
									fontSize: "clamp(20px, 2vw, 24px)",
								}}>
								{formatPackageName(currentBestOffer)}
							</Typography>
						</div>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<Typography
								variant="h6"
								style={{
									fontWeight: "normal",
									color: "#333",
									fontSize: "clamp(16px, 2vw, 18px)",
								}}>
								{`${currentBestOffer.service.duration}min`}
							</Typography>
							<Typography
								variant="h6"
								style={{
									fontWeight: "normal",
									color: "#333",
									fontSize: "clamp(16px, 2vw, 18px)",
								}}>
								{formatPrice(currentBestOffer.price)}
							</Typography>
						</div>
					</Box>
				)}

				<Box style={{ display: "flex", gap: "12px", paddingBottom: 20 }}>
					{currentServicePackages && currentServicePackages.length > 1 ? (
						<Button
							variant="outlined"
							onClick={() => handleComponentView("otherPackages")}
							disabled={isActivating}
							style={{
								backgroundColor: "#F2E9D8",
								color: "#222",
								borderRadius: "30px",
								padding: "6px",
								fontWeight: "normal",
								fontSize: "20px",
								textTransform: "none",
								boxShadow: "none",
								flex: 1,
								border: "none",
							}}>
							{t("View packages")}
						</Button>
					) : (
						<Button
							variant="outlined"
							onClick={() => handleAcceptOffer(currentBestOffer.id)}
							disabled={isActivating}
							style={{
								backgroundColor: "#F2E9D8",
								color: "#222",
								borderRadius: "30px",
								padding: "6px",
								fontWeight: "normal",
								fontSize: "20px",
								textTransform: "none",
								boxShadow: "none",
								flex: 1,
								border: "none",
							}}>
							{t("Activate package")}
						</Button>
					)}

					<Button
						variant="outlined"
						onClick={handleDeclineOffer}
						disabled={isActivating}
						style={{
							backgroundColor: "#F2E9D8",
							color: "#222",
							borderRadius: "30px",
							padding: "6px",
							fontWeight: "normal",
							fontSize: "20px",
							textTransform: "none",
							boxShadow: "none",
							flex: 1,
							border: "none",
						}}>
						{t("Schedule without package")}
					</Button>
				</Box>
			</>
		);
	};

	const componentOtherPackages = () => {
		return (
			<>
				<Typography
					variant="h6"
					component="h2"
					style={{
						fontWeight: "bold",
						color: "#333",
						marginBottom: "12px",
						fontSize: "clamp(22px, 2vw, 26px)",
						textAlign: "left",
					}}>
					{t("Enjoy our packages and finish your booking today")}
				</Typography>
				{currentServicePackages.map((packageData) => {
					const isSelected = selectedPackageId === packageData.id;
					return (
						<Box
							key={packageData.id}
							style={{
								backgroundColor: "#fff",
								borderRadius: "8px",
								padding: "16px",
								marginBottom: "10px",
								border: isSelected ? "2px solid #672E37" : "1px solid #e9ecef",
								display: "flex",
								gap: "12px",
								cursor: "pointer",
							}}
							onClick={() => setSelectedPackageId(packageData.id)}>
							<div style={{ paddingTop: 4 }}>
								<CheckCircleIcon style={{ color: isSelected ? "#672E37" : "#ccc" }} />
							</div>
							<div>
								<div style={{ display: "flex", justifyContent: "start", alignItems: "center" }}>
									<Typography
										variant="subtitle1"
										style={{
											fontWeight: "bold",
											color: "#333",
											fontSize: "clamp(18px, 2vw, 22px)",
										}}>
										{formatPackageName(packageData)}
									</Typography>
								</div>
								<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<Typography
										variant="h6"
										style={{
											fontWeight: "normal",
											color: "#333",
											fontSize: "clamp(16px, 2vw, 18px)",
										}}>
										{`${packageData.service.duration}min`}
									</Typography>
									<Typography
										variant="h6"
										style={{
											fontWeight: "normal",
											color: "#333",
											fontSize: "clamp(16px, 2vw, 18px)",
										}}>
										{formatPrice(packageData.price)}
									</Typography>
								</div>
							</div>
						</Box>
					);
				})}

				<Box style={{ display: "flex", gap: "12px", paddingBottom: 20 }}>
					<Button
						variant="outlined"
						onClick={() => selectedPackageId && handleAcceptOffer(selectedPackageId)}
						disabled={!selectedPackageId || isActivating}
						style={{
							backgroundColor: "#672E37",
							color: "#fff",
							opacity: !selectedPackageId || isActivating ? 0.6 : 1,
							borderRadius: "30px",
							padding: "6px",
							fontWeight: "normal",
							fontSize: "20px",
							textTransform: "none",
							boxShadow: "none",
							flex: 1,
							border: "none",
						}}>
						{t("Activate package and schedule")}
					</Button>
				</Box>
			</>
		);
	};

	const selectComponentPackage = (type) => {
		switch (type) {
			case "only":
				return componentOnlyPackage();
			case "otherPackages":
				return componentOtherPackages();
			default:
				return null;
		}
	};

	// Determine visibility
	const hasAnyPackages = recommendedPackages && recommendedPackages.length > 0;
	if (!open) return null;
	if (!isAnonymousFlow && !hasAnyPackages) return null;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth={false}
			keepMounted
			TransitionComponent={Transition}
			className={BOTTOM_SHEET_FIXED_CLASS}
			classes={{ container: sheetClasses.container }}
			PaperProps={{
				style: {
					width: "100%",
					maxWidth: 480,
					margin: 0,
					borderTopLeftRadius: 18,
					borderTopRightRadius: 18,
					borderBottomLeftRadius: 0,
					borderBottomRightRadius: 0,
					padding: 0,
					backgroundColor: "#fff",
				},
			}}
			BackdropProps={{
				style: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
			}}>
			<DialogContent style={{ padding: "24px", textAlign: "center", position: "relative" }}>
				<div style={{ position: "absolute", top: 8, right: 8 }}>
					<IconButton aria-label="close" onClick={onClose}>
						<CloseIcon style={{ width: 16, height: 16 }} />
					</IconButton>
				</div>
				<div style={{ padding: "10px 0", textAlign: isAnonymousFlow ? "left" : "center" }}>
					{isAnonymousFlow
						? componentAnonymousFlow()
						: selectComponentPackage(componentView)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default PackageOfferModal;
